/**
 * My Trainings — the employee/contractor view of the training-compliance module:
 * which trainings I must take, their status, and a deep link to take the test in
 * Dojo. Managers see this same view (training is not a manager surface).
 *
 * Live data via `useMyTrainings()` (GET /api/trainings/my). The approved mockup
 * design is the visual contract — only the data source is swapped here.
 */
import { useState } from 'react';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import { Button } from '../../components/common/Button';
import { AcademicCapIcon, ArrowRightIcon } from '../../components/ui/icons';
import { PaginationFooter } from '../admin/TrainingAdminPage';
import { useMyTrainings } from '../../hooks/useTrainings';
import type { TrainingStatusDTO } from '../../services/api/training';

// Mirrors the admin Compliance model: a mutually-exclusive compliance state
// plus a separate `dueSoon` flag (a deadline within the reminder window). A
// person can be Done AND have a renewal Due soon — those show together.
type State = TrainingStatusDTO['state'];

const STATE_META: Record<State, { label: string; pill: string }> = {
  compliant: { label: 'Done', pill: 'bg-green-100 text-green-800' },
  not_started: { label: 'Not started', pill: 'bg-gray-100 text-gray-600' },
  overdue: { label: 'Overdue', pill: 'bg-red-100 text-red-800' },
};

const fmtDate = (iso: string): string => format(new Date(iso), 'd MMM yyyy', { locale: cs });

// Server returns `note: null` today; derive the same one-line summary the mockup
// showed from the live fields so the card body stays populated.
function noteFor(t: TrainingStatusDTO): string {
  if (t.note) return t.note;
  const latestScore = t.completions[0]?.score ?? null;
  if (t.state === 'overdue') {
    return t.nextDueAt ? `Overdue · was due ${fmtDate(t.nextDueAt)}` : 'Overdue';
  }
  if (t.state === 'compliant') {
    const parts = ['Completed'];
    if (t.nextDueAt) parts.push(`renews ${fmtDate(t.nextDueAt)}`);
    if (latestScore != null) parts.push(`last score ${latestScore}%`);
    return parts.join(' · ');
  }
  return t.nextDueAt ? `Not started · due ${fmtDate(t.nextDueAt)}` : 'Not started';
}

interface HistoryRow {
  title: string;
  date: string;
  score: number | null;
  result: 'Passed';
}

// Flatten every course's pass history into one reverse-chronological table.
function buildHistory(trainings: TrainingStatusDTO[]): HistoryRow[] {
  return trainings
    .flatMap((t) =>
      t.completions.map((c) => ({
        title: t.title,
        passedAt: c.passedAt,
        score: c.score,
      })),
    )
    .sort((a, b) => new Date(b.passedAt).getTime() - new Date(a.passedAt).getTime())
    .map((c) => ({
      title: c.title,
      date: fmtDate(c.passedAt),
      score: c.score,
      result: 'Passed' as const,
    }));
}

export function MyTrainingsPage() {
  const { data: trainings = [], isLoading, isError } = useMyTrainings();

  // Needs attention = not compliant, or compliant but a renewal is due soon.
  const openCount = trainings.filter((t) => t.state !== 'compliant' || t.dueSoon).length;
  const history = buildHistory(trainings);

  // Real pagination over the (already-loaded, per-user) history. A separate
  // paginated endpoint would be overkill for one person's own pass history;
  // slicing the loaded list is correct here. The cross-user admin Compliance
  // matrix, by contrast, paginates server-side.
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const historyTotalPages = Math.max(1, Math.ceil(history.length / historyPageSize));
  const historyPageClamped = Math.min(historyPage, historyTotalPages);
  const pagedHistory = history.slice(
    (historyPageClamped - 1) * historyPageSize,
    historyPageClamped * historyPageSize,
  );

  const subtitle = isLoading
    ? 'Loading your trainings…'
    : isError
      ? 'Could not load your trainings — please try again.'
      : openCount === 0
        ? 'You are all caught up — nice.'
        : `You have ${openCount} training${openCount > 1 ? 's' : ''} that ${openCount > 1 ? 'need' : 'needs'} attention.`;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <AcademicCapIcon className="h-7 w-7 text-primary-600" />
        <h1 className="text-2xl font-semibold text-gray-900">My Trainings</h1>
      </div>
      <p className="-mt-3 text-sm text-gray-500">{subtitle}</p>

      {/* Required trainings */}
      <div className="space-y-3">
        {trainings.map((t) => {
          const meta = STATE_META[t.state];
          // Accent draws the eye: overdue → red, else a nearing deadline → amber,
          // else compliant → green.
          const accent =
            t.state === 'overdue'
              ? 'border-l-red-400'
              : t.dueSoon
                ? 'border-l-amber-400'
                : t.state === 'compliant'
                  ? 'border-l-green-400'
                  : 'border-l-gray-300';
          const needsAction = t.state !== 'compliant' || t.dueSoon;
          const label =
            t.state === 'compliant' ? (t.dueSoon ? 'Renew now' : 'Retake') : 'Take the test';
          return (
            <div
              key={t.slug}
              className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border border-l-4 border-gray-200 bg-white px-4 py-4 ${accent}`}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-gray-900">{t.title}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${meta.pill}`}>
                    {meta.label}
                  </span>
                  {t.dueSoon && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                      Due soon
                    </span>
                  )}
                </div>
                <div className="mt-1 text-sm text-gray-500">{noteFor(t)}</div>
              </div>
              <a href={t.dojoUrl} target="_blank" rel="noopener noreferrer">
                <Button size="sm" variant={needsAction ? 'primary' : 'secondary'}>
                  {label}
                  <ArrowRightIcon className="ml-1 h-4 w-4" />
                </Button>
              </a>
            </div>
          );
        })}
      </div>

      {/* History */}
      <div>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray-500">
          Completion history
        </h2>
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-3">Course</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Score</th>
                <th className="px-4 py-3">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pagedHistory.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-400">
                    No completions yet.
                  </td>
                </tr>
              ) : (
                pagedHistory.map((h, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3 text-gray-900">{h.title}</td>
                    <td className="px-4 py-3 text-gray-500">{h.date}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {h.score != null ? `${h.score}%` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                        {h.result}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <PaginationFooter
            total={history.length}
            page={historyPageClamped}
            pageSize={historyPageSize}
            totalPages={historyTotalPages}
            onPageChange={setHistoryPage}
            onPageSizeChange={(size) => {
              setHistoryPageSize(size);
              setHistoryPage(1);
            }}
          />
        </div>
      </div>
    </div>
  );
}
